import angular from "angular";

import { CalculationReportData, Datasets, Dates, Numeric, PeriodType, PeriodDateKind, Reports, Texts, LegalEntityType } from "@salaxy/core";

import { SessionService, UiHelpers } from "../../services";
import { ConfigurableGridReportControllerBase } from "./ConfigurableGridReportControllerBase";

/**
 * Controller for calculation reports.
 */
export class CalculationReportController extends ConfigurableGridReportControllerBase<CalculationReportData> {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["Datasets", "UiHelpers", "SessionService", "Reports", "$timeout"];

  /** Usecase uri for report templates */
  public usecaseUri = "palkkaus.fi/reports/calculationReport";

  /** Current grid options. */
  public gridOptions: any = {
    statusBar: {
      statusPanels: [
        { statusPanel: "agTotalAndFilteredRowCountComponent", key: "totalAndFilter", align: "left" },
        { statusPanel: "agSelectedRowCountComponent", align: "left" },
        { statusPanel: "agAggregationComponent", align: "right" }
      ]
    },
    enableMultiRowDragging: true,
    rowGroupPanelShow: "always",
    pivotPanelShow: "always",
    enableCharts: true,
    enableRangeSelection: true,

    defaultExportParams: {
      columnGroups: true,
      headerRowHeight: 30,
      rowHeight: 22,
      fontSize: 14,
    },
    defaultColDef: {
      flex: 1,
      minWidth: 200,
      sortable: true,
      filter: true,
      resizable: true,
      floatingFilter: true,
    },
    sideBar: { toolPanels: ["columns", "filters"] },
    rowData: [],
  };

  /** ID for the element containing grid. */
  public gridElmentId = "#dataGrid";

  /** Default templates */
  public defaultTemplates = [
      {
        id: "incomeTypes",
        owner: null,
        label: "Tulolajiraportti",
        config: null,
        isReadOnly: true,
        avatar: {
          initials: "TLR",
          entityType: LegalEntityType.Company,
          color: "#009fd8"
        }
      },
      {
        id: "totals",
        owner: null,
        label: "Palkanmaksujen erittely",
        config: null,
        isReadOnly: true,
        avatar: {
          initials: "PME",
          entityType: LegalEntityType.Company,
          color: "#009fd8"
        }
      },
      {
        id: "plain",
        owner: null,
        label: "Kaikki sarakkeet",
        config: {},
        isReadOnly: true,
        avatar: {
          initials: "Kasa",
          entityType: LegalEntityType.Company,
          color: "#009fd8"
        }
      }
  ];

  constructor(datasets: Datasets, uiHelpers: UiHelpers, sessionService: SessionService, private reports: Reports,  private $timeout: angular.ITimeoutService) {
    super(datasets, uiHelpers, sessionService);
  }

  /**
   * Initialize controller values.
   */
  public $onInit() {
    super.$onInit();
  }

  /**
   * Processes and sets the data.
   */
  public setData(result: CalculationReportData) {
    this.data = result;

    const sortingDateComparator = (a, b) => {
      if (!a || !b) {
        return a === b ? 0 : a ? 1 : -1;
      }
      const mA = Dates.asDate(a);
      const mB = Dates.asDate(b);
      return mA === mB ? 0 : mA > mB ? 1 : -1;
    };
    const filterDateComparator = (a, b) => sortingDateComparator(b, a);

    const columnDefs = [];
    columnDefs.push(
      {
        headerName: "Laskelmat",
        children: [
          {
            headerName: "Työntekijä",
            rowDrag: true,
            field: "worker",
            enableRowGroup: true,
            filter: "agMultiColumnFilter",
          },
          {
            headerName: "Laskelma",
            // rowDrag: true,
            field: "calculation",
            // enableRowGroup: true,
            valueGetter: (params) => {
              if (params.node && params.node.data) {
                return `${params.node.data.worker} ${params.node.data.salaryDate} ${Numeric.formatPrice(params.node.data["totals-totalGrossSalary"])}`;
              }
              return "";
            },
            cellRenderer: (params) => {
              if (params.node && params.node.data) {
                return `<a href="#/calc/details/${params.node.data.calculationId}" target="_blank" rel="noopener">${Texts.escapeHtml(params.node.data.worker)} ${params.node.data.salaryDate} ${Numeric.formatPrice(params.node.data["totals-totalGrossSalary"])}</a>`;
              }
              return "";
            }
          },
          {
            headerName: "Työntekijän syntymäaika",
            field: "workerBirthDate",
            valueFormatter: params => Dates.getFormattedDate(params.value),
            type: ["rightAligned"],
            filter: "agDateColumnFilter",
            comparator: sortingDateComparator,
            filterParams: { comparator: filterDateComparator, },
          },
          {
            headerName: "Palkkapäivä",
            field: "salaryDate",
            valueFormatter: params => Dates.getFormattedDate(params.value),
            type: ["rightAligned"],
            filter: "agDateColumnFilter",
            comparator: sortingDateComparator,
            filterParams: { comparator: filterDateComparator, },
          },
          {
            headerName: "Brutto",
            field: "totalGrossSalary",
            valueGetter: (params) => {
              if (params.node && params.node.data) {
                return params.node.data["totals-totalGrossSalary"];
              }
              return null;
            },
            type: ["numericColumn"],
            valueFormatter: params => Numeric.formatNumber(params.value, 2),
            filter: "agNumberColumnFilter",
          },
          {
            headerName: "Kuvaus",
            field: "workDescription",
            filter: "agMultiColumnFilter",
          },
          {
            headerName: "Aloituspäivä",
            field: "workStartDate",
            valueFormatter: params => Dates.getFormattedDate(params.value),
            type: ["rightAligned"],
            filter: "agDateColumnFilter",
            comparator: sortingDateComparator,
            filterParams: { comparator: filterDateComparator, },
          },
          {
            headerName: "Lopetuspäivä",
            field: "workEndDate",
            valueFormatter: params => Dates.getFormattedDate(params.value),
            type: ["rightAligned"],
            filter: "agDateColumnFilter",
            comparator: sortingDateComparator,
            filterParams: { comparator: filterDateComparator, },
          },
          {
            headerName: "Työsuhteen aloituspäivä",
            field: "workerEmploymentStartDate",
            valueFormatter: params => Dates.getFormattedDate(params.value),
            type: ["rightAligned"],
            filter: "agDateColumnFilter",
            comparator: sortingDateComparator,
            filterParams: { comparator: filterDateComparator, },
          },
          {
            headerName: "Työsuhteen päättymispäivä",
            field: "workerEmploymentEndDate",
            valueFormatter: params => Dates.getFormattedDate(params.value),
            type: ["rightAligned"],
            filter: "agDateColumnFilter",
            comparator: sortingDateComparator,
            filterParams: { comparator: filterDateComparator, },
          },
          {
            headerName: "Määräaikainen työsuhde",
            field: "workerEmploymentIsFixedTerm",
            filter: "agMultiColumnFilter",
          },
        ]
      },
      {
        headerName: "Ryhmittelyt",
        children: [
          {
            headerName: "Kuukausi",
            field: "salaryMonth",
            enablePivot: true,
            enableRowGroup: true,
            type: ["numericColumn"],
            filter: "agMultiColumnFilter",
          },
          {
            headerName: "Vuosi",
            field: "salaryYear",
            enablePivot: true,
            enableRowGroup: true,
            type: ["numericColumn"],
            filter: "agMultiColumnFilter",
          },
          {
            headerName: "Ammattiluokka",
            field: "occupationCode",
            enablePivot: true,
            enableRowGroup: true,
            filter: "agMultiColumnFilter",
          },
          {
            headerName: "Eläketurva",
            field: "pensionCalculation",
            enablePivot: true,
            enableRowGroup: true,
            filter: "agMultiColumnFilter",
          },
        ],
      }
    );
    const irGroupDef = {
      headerName: "Tulolajit",
      children: [],
    };
    columnDefs.push(irGroupDef);
    Object.keys(this.data.irLabels).forEach((key) => {
      irGroupDef.children.push({
        field: key,
        headerName: this.data.irLabels[key],
        enableValue: true,
        type: ["numericColumn"],
        valueFormatter: params => Numeric.formatNumber(params.value, 2),
        filter: "agNumberColumnFilter",
      });
    });

    const typeGroupDef = {
      headerName: "Rivityypit",
      children: [],
    };
    columnDefs.push(typeGroupDef);
    Object.keys(this.data.typeLabels).forEach((key) => {
      typeGroupDef.children.push({
        field: key,
        headerName: this.data.typeLabels[key],
        enableValue: true,
        type: ["numericColumn"],
        valueFormatter: params => Numeric.formatNumber(params.value, 2),
        filter: "agNumberColumnFilter",
      });
    });

    const employerCalcDef = {
      headerName: "Työnantaja",
      children: [],
    };
    const workerCalcDef = {
      headerName: "Työntekijä",
      children: [],
    };
    const totalsDef = {
      headerName: "Yhteensä",
      children: [],
    };
    const totalGroupDef = {
      headerName: "Maksut",
      children: [
        workerCalcDef,
        employerCalcDef,
        totalsDef,
      ],
    };
    columnDefs.push(totalGroupDef);
    Object.keys(this.data.totalLabels).forEach((key) => {
      const def = key.indexOf("employerCalc") === 0 ? employerCalcDef :
        key.indexOf("workerCalc") === 0 ? workerCalcDef : totalsDef;

      def.children.push({
        field: key,
        headerName: this.data.totalLabels[key],
        enableValue: true,
        type: ["numericColumn"],
        valueFormatter: params => Numeric.formatNumber(params.value, 2),
        filter: "agNumberColumnFilter",
      });
    });

    // Create presets if not created.
    const incomeTypes = this.getTemplate("incomeTypes");
    incomeTypes.config = incomeTypes.config ?? { columnState: this.createIncomeTypesPreset(columnDefs, this.data.rows)};

    const totals = this.getTemplate("totals");
    totals.config = totals.config ?? { columnState: this.createTotalsPreset(columnDefs)};

    // create the grid passing in the div to use together with the columns & data we want to use
    this.$timeout(() => {
      const filterModel = this.gridOptions.api.getFilterModel();
      this.gridOptions.api.setFilterModel(null);
      this.gridOptions.api.setRowData(this.data.rows);
      this.gridOptions.api.setColumnDefs(columnDefs);
      this.gridOptions.api.setFilterModel(filterModel);
      this.selectTemplate(this.templateId ?? "incomeTypes");
    });
  }

  /**
   * Gets the monthly / quarterly / yearly cumulative data for the current account.
   * @param refDate Reference date for the period. Please note that even if the date is not the first day of the given period, the entire period is returned.
   * @param periodType Month, quarter, year or a custom period. The custom period requires endDate. Default value is the month.
   * @param endDate End date for the period. Required only for the custom period.
   * @param periodDateKind Period date type: paid at date, salary date or work date.
   * @param workers List of worker official ids.
   * @returns A Promise with result data.
   */
  public queryDataForPeriod(refDate: string, periodType?: PeriodType | "all", endDate?: string, periodDateKind?: PeriodDateKind, workers?: string[]): Promise<CalculationReportData> {

    if (periodType === "all") {
      refDate = "2010-01-01";
      endDate = "2100-01-01";
      periodType = PeriodType.Custom;
    }
    return this.reports.getCalculationReportData(refDate,periodType,endDate, periodDateKind, workers);
  }

  /**
   * Gets the cumulative data based on given set of calculations.
   * @param calculationIds - Calculations that are the bases for the report.
   * @returns A Promise with result data.
   */
  public queryDataForCalculationIds(calculationIds: string[]): Promise<CalculationReportData> {
    return this.reports.getCalculationReportDataForCalculationIds(calculationIds);
  }

  private createIncomeTypesPreset(columnDefs: any, rows: any[]) {
    const preset = [];

    // Laskelmat
    const calculationDef = columnDefs[0];
    for (const def of calculationDef.children) {
      switch (def.field) {
        case "worker":
          preset.push(this.createPresetCol(def.field, true, true));
          break;
        case "calculation":
          preset.push(this.createPresetCol(def.field, false));
          break;
        default:
          preset.push(this.createPresetCol(def.field, true));
      }
    }
    // Dimensiot
    const dimensionDef = columnDefs[1];
    for (const def of dimensionDef.children) {
      preset.push(this.createPresetCol(def.field, true));
    }
    // Tulolajit
    const incomeTypeDef = columnDefs[2];
    for (const def of incomeTypeDef.children) {
      const hide = !(rows ?? []).find( x => x[def.field]);
      if (def.field.endsWith("-price")) {
        preset.push(this.createPresetCol(def.field, hide, false, "avg"));
      } else {
        preset.push(this.createPresetCol(def.field, hide, false, "sum"));
      }
    }

    // Rivityypit
    const rowTypeDef = columnDefs[3];
    for (const def of rowTypeDef.children) {
      preset.push(this.createPresetCol(def.field, true));
    }

    // Maksut - työntekijä
    const workerTotalsDef = columnDefs[4].children[0];
    for (const def of workerTotalsDef.children) {
      preset.push(this.createPresetCol(def.field, true));
    }

    // Maksut - työnantaja
    const employerTotalsDef = columnDefs[4].children[1];
    for (const def of employerTotalsDef.children) {
      preset.push(this.createPresetCol(def.field, true));
    }

    // Maksut - yhteensä
    const totalsDef = columnDefs[4].children[2];
    for (const def of totalsDef.children) {
      preset.push(this.createPresetCol(def.field, true));
    }

    return preset;
  }

  private createTotalsPreset(columnDefs: any) {
    const preset = [];

    // Laskelmat
    const calculationDef = columnDefs[0];
    for (const def of calculationDef.children) {
      switch (def.field) {
        case "worker":
          preset.push(this.createPresetCol(def.field, true, true));
          break;
        case "salaryDate":
        case "workStartDate":
        case "workEndDate":
          preset.push(this.createPresetCol(def.field, false));
          break;
        default:
          preset.push(this.createPresetCol(def.field, true));
          break;
      }
    }
    // Dimensiot
    const dimensionDef = columnDefs[1];
    for (const def of dimensionDef.children) {
      switch (def.field) {
        case "pensionCalculation":
          preset.push(this.createPresetCol(def.field, false));
          break;
        default:
          preset.push(this.createPresetCol(def.field, true));
          break;
      }
    }
    // Tulolajit
    const incomeTypeDef = columnDefs[2];
    for (const def of incomeTypeDef.children) {
      preset.push(this.createPresetCol(def.field, true));
    }

    // Rivityypit
    const rowTypeDef = columnDefs[3];
    for (const def of rowTypeDef.children) {
      preset.push(this.createPresetCol(def.field, true));
    }

    const totalFields = [
      "totals-totalGrossSalary",
      "workerCalc-benefits",
      "totals-annualHolidayCompensation",
      "totals-holidayBonus",
      "totals-holidaySalary",
      // "muut vähennettävät erät ml. luontoiseduista vähennettävät"
      "totals-totalTaxable",
      "workerCalc-tax",
      "workerCalc-pension",
      "workerCalc-unemploymentInsurance",
      "workerCalc-unionPayment",
      "workerCalc-foreclosure",
      "workerCalc-salaryAdvance",
      "workerCalc-otherDeductions",
      "workerCalc-deductions",
      "workerCalc-prepaidExpenses",
      "totals-totalExpenses",
      "workerCalc-totalWorkerPayment",
      "employerCalc-totalPayment",
      "employerCalc-palkkaus",
      "employerCalc-service",
      "employerCalc-socialSecurity",
      "totals-pension",
      "employerCalc-pension",
      "totals-unemployment",
      "employerCalc-unemployment",
      "totals-totalPensionInsuranceBase",
      "totals-totalSocialSecurityBase",
      "totals-totalHealthInsuranceBase",
      "totals-totalUnemploymentInsuranceBase",
      "totals-totalAccidentInsuranceBase",
      "totals-totalAccidentInsurance",
      "totals-totalGroupLifeInsurance"
    ];

    for (const field of totalFields) {
      preset.push(this.createPresetCol(field, false, false, "sum"));
    }

    // Maksut - työntekijä
    const workerTotalsDef = columnDefs[4].children[0];
    for (const def of workerTotalsDef.children) {
      if (totalFields.indexOf(def.field) < 0) {
        preset.push(this.createPresetCol(def.field, true));
      }
    }

    // Maksut - työnantaja
    const employerTotalsDef = columnDefs[4].children[1];
    for (const def of employerTotalsDef.children) {
      if (totalFields.indexOf(def.field) < 0) {
        preset.push(this.createPresetCol(def.field, true));
      }
    }

    // Maksut - yhteensä
    const totalsDef = columnDefs[4].children[2];
    for (const def of totalsDef.children) {
      if (totalFields.indexOf(def.field) < 0) {
        preset.push(this.createPresetCol(def.field, true));
      }
    }

    return preset;
  }

  private createPresetCol(colId: string, hide: boolean, rowGroup = false, aggFunc: string = null) {
    return {
      aggFunc,
      colId,
      flex: 1,
      hide,
      pinned: null,
      pivot: false,
      pivotIndex: null,
      rowGroup,
      rowGroupIndex: null,
      sort: null,
      sortIndex: null,
      width: 200,
    };
  }
}
